import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent } from '@datadog/browser-rum';
import { vuePlugin } from '@datadog/browser-rum-vue';
import {
  isProductionEnvironment,
  parseHostname,
  redactLinkUrl,
} from '@sera/core';
import {
  DATA_IMAGE_DETECT,
  redactDataImages,
  redactDataImagesDeep,
} from './redactDataImages';

/**
 * Datadog RUM (Real User Monitoring) initialization for the Customer Portal.
 *
 * Mirrors the admin-web RUM setup (api/app/javascript/lib/datadog.js) but adapts
 * it to this workspace's runtime-env-resolution model: there is a single build
 * artifact and the environment is derived from `window.location.hostname` at
 * runtime (see libs/core/src/env/hostname.ts), NOT baked in per-build. That is
 * why the `env` RUM dimension is computed here rather than read from VITE_ENV
 * (which is unset in deployed builds — a separate, pre-existing gap that also
 * affects Sentry's env tag; tracked as a follow-up on the RUM ticket).
 *
 * Gating mirrors initSentry: this is a no-op until the RUM application is
 * provisioned in Datadog and its (public, client-side) credentials are supplied
 * via the VITE_DD_RUM_* env vars, and it never runs in local dev.
 *
 * User identity + tenant context is attached separately, in libs/core's
 * `datadog/context.ts` (wired into the auth store next to identifySentry), for
 * the same dependency-direction reason documented there: the auth store is a
 * leaf in libs/core and cannot import this app-layer plugin.
 *
 * Vue integration uses the official `@datadog/browser-rum-vue` plugin:
 * - `vuePlugin({ router: true })` here, PLUS the wrapped `createRouter` from
 *   `@datadog/browser-rum-vue/vue-router-v4` in src/router — both are required
 *   together. `router: true` flips RUM to manual view tracking, so views come
 *   exclusively from the router's afterEach hook (with normalized names for
 *   dynamic routes, e.g. `/invoices/:id` instead of `/invoices/123`).
 * - `addVueError` is installed as the Vue `errorHandler` in main.ts.
 */

/**
 * Resolve the Datadog `env` dimension from the current hostname.
 * - production hostname (no env segment) → 'prod'
 * - env-segment hostname (staging/blue/green/…) → that segment
 * - anything else → 'unknown'
 * Only ever called from a deployed build (init bails in dev before this runs).
 */
export function resolveRumEnv(): string {
  if (isProductionEnvironment()) return 'prod';
  const { envSegment, hasEnvSegment } = parseHostname(window.location.hostname);
  return hasEnvSegment ? envSegment : 'unknown';
}

/**
 * Loopback host detection — true when the app is served from localhost, i.e.
 * the E2E preview server (`nx run customer-portal:preview` on :4300) or any
 * local `vite preview`. A deployed Sera host is always `tenant[.env].schedule
 * .online`, never a loopback address. This is the reliable "not a real deploy"
 * signal: `import.meta.env.DEV` is FALSE in a preview build, and on localhost
 * `isProductionEnvironment()` returns TRUE (no env segment ⇒ prod), so without
 * this check RUM would boot in E2E and report as env:'prod'.
 */
function isLoopbackHost(): boolean {
  const h = window.location.hostname;
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]' ||
    h.endsWith('.localhost')
  );
}

const REDACTED_ERROR_FIELDS = ['message', 'stack', 'handling_stack'] as const;

/** True when any string under `value` still carries a data:image URL. */
function carriesDataImage(value: unknown): boolean {
  try {
    return DATA_IMAGE_DETECT.test(JSON.stringify(value) ?? '');
  } catch {
    // Unserializable: we cannot prove it is clean, so treat it as dirty.
    return true;
  }
}

/**
 * Phase 12 (SIGNSAFE-05, D-19) — RUM `beforeSend` hook.
 *
 * RUM error collection consumes `console.error` and uncaught errors, so a
 * logged or thrown string carrying the drawn signature (`data:image/...`) would
 * otherwise ship in `error.message` / `error.stack`. Datadog's documented
 * beforeSend contract is in-place mutation of a fixed list of fields, so the
 * modifiable ones are rewritten here: `context` on every event, plus
 * `error.message`, `error.stack`, and `error.handling_stack` on errors.
 *
 * WR-07: the SDK silently discards beforeSend writes to any other field (see
 * `MODIFIABLE_FIELD_PATHS_BY_EVENT` in `@datadog/browser-rum-core`), notably
 * `error.causes`, which carries the `Error.cause` chain. Those cannot be
 * scrubbed, so an error event that still carries `data:image/` anywhere under
 * `error` after redaction is dropped (fail closed). Every other event is kept.
 * Replay is covered separately by `data-dd-privacy="hidden"` on the signing
 * surfaces (Plan 12-01).
 */
export function redactRumEvent(event: RumEvent): boolean {
  // The SDK types these as readonly; beforeSend is the sanctioned place to
  // modify them.
  const mutable = event as { context?: unknown };
  if (mutable.context && carriesDataImage(mutable.context)) {
    const redacted = redactDataImagesDeep(mutable.context);
    // The deep walk skips class instances; if any data URL survives, drop
    // the whole context rather than the event (view events cannot be dropped).
    mutable.context = carriesDataImage(redacted) ? {} : redacted;
  }
  if (event.type !== 'error') return true;
  const error = event.error as Record<string, unknown>;
  for (const field of REDACTED_ERROR_FIELDS) {
    const value = error[field];
    if (typeof value === 'string') error[field] = redactDataImages(value);
  }
  return !carriesDataImage(error);
}

export function initDatadogRum(): void {
  // The RUM application ID + client token are public, client-side identifiers
  // (they ship in the browser bundle, exactly like the Sentry DSN). They are
  // provisioned per the RUM ticket via GitLab CI project variables, which Vite
  // inlines at build time.
  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN;
  const isDev = import.meta.env.DEV;
  // Automated browsers (Playwright/E2E). The cross-browser E2E job runs a
  // PRODUCTION build (isDev false) served on localhost, so isDev alone let RUM
  // boot during E2E: it initialized as env:'prod' (localhost has no env segment
  // ⇒ isProductionEnvironment() true), shipped synthetic session replays into
  // the production RUM app, and its boot-time work (replay recorder, resource/
  // long-task observers) raced the Vue mount that fires our onMounted analytics
  // — flaking the invoice_detail_viewed E2E (ENG-139 follow-up). Gate on both
  // webdriver and loopback so RUM is off for automation and any local preview.
  const isAutomated =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  // No-op until credentials are provisioned, and never in local dev, an
  // automated browser, or a loopback (preview) host — RUM is strictly for real
  // end users on a deployed host. Mirrors initSentry's `!!VITE_SENTRY_DSN &&
  // !isDev` gate and the admin-web `applicationId && clientToken &&
  // !['development','test'].includes(env)` guard.
  if (
    !applicationId ||
    !clientToken ||
    isDev ||
    isAutomated ||
    isLoopbackHost()
  )
    return;

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: 'customer-portal',
    env: resolveRumEnv(),
    sessionSampleRate: 100,
    // Session Replay sampling, dialed to 10% for RUM cost control. Safe to
    // enable here because defaultPrivacyLevel: 'mask' (below) masks ALL
    // text/inputs in the replay — stricter than admin-web's 'mask-user-input' —
    // which matters for the financial/PII surfaces this portal renders. Dial
    // down or to 0 if a privacy review of the payment/quote screens calls for it.
    // Replay records page URLs without passing through beforeSend, so a session
    // that lands on a pay or quotes link (the token is in the path) is never replayed.
    sessionReplaySampleRate: isLinkPage(window.location.pathname) ? 0 : 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    // Official Vue integration. Requires the wrapped createRouter in src/router
    // (see this file's header) — together they enable normalized SPA view names.
    plugins: [vuePlugin({ router: true })],
    // Scrub link tokens from URLs, then signature data URLs from error payloads.
    beforeSend: (event) => redactRumUrls(event) && redactRumEvent(event),
  });
}

// Where RUM keeps URLs; fetch and network errors also quote the URL in their text.
const RUM_URL_FIELDS = [
  ['view', 'url'],
  ['view', 'referrer'],
  ['resource', 'url'],
  ['error', 'message'],
  ['error', 'stack'],
] as const;

/**
 * Strips pay and quotes link tokens and `temp_token` values from the URLs RUM
 * records. The page URL carries the link token and every link-scoped API
 * call carries a `temp_token`; either one opens that customer's documents.
 */
export function redactRumUrls(event: RumEvent): boolean {
  const e = event as Record<string, Record<string, unknown> | undefined>;
  for (const [group, field] of RUM_URL_FIELDS) {
    redactField(e[group], field);
  }
  redactField(e.error?.resource as Record<string, unknown> | undefined, 'url');
  return true;
}

function redactField(
  target: Record<string, unknown> | undefined,
  field: string,
): void {
  const value = target?.[field];
  if (target && typeof value === 'string') target[field] = redactLinkUrl(value);
}

/** Public pages whose path carries a link token. */
export function isLinkPage(pathname: string): boolean {
  return /^\/(?:pay|links)\/[^/]/.test(pathname);
}
